/**
 * P1-ENGINE D3 — retrieve-then-adjust: ranked precedent to complete
 * recommendation, with explicit bounded adjustments only.
 *
 * Binding rules (P1 card deliverable 3 + Phase 0 contracts):
 * - Bounded adjustments ONLY (length/waist within explicit rule bounds).
 * - Provenance per field (schemas.ts RecommendationField).
 * - Fallback honesty: defer-to-designer, never force (evaluation-metrics.md).
 * - Determinism: pure functions, no clock/RNG.
 */
import {
  type ReferenceDesign,
  type ReferenceLibrary,
  type RankedReference,
  rankByDistance,
} from './referenceLibrary';
import type {
  DesignRecommendation,
  RecommendationField,
  SkiType,
} from './schemas';

export const ADJUSTMENT_RULES_VERSION = '0.1';

export interface AdjustmentRules {
  maxDeltaLengthCm: number;
  maxDeltaWaistMm: number;
  maxPrecedentDistance: number;
}

/** v0.1 bounds; TYPE_MISMATCH=100 dominates, so 60 defers before forcing. */
export const DEFAULT_ADJUSTMENT_RULES: AdjustmentRules = {
  maxDeltaLengthCm: 5,
  maxDeltaWaistMm: 8,
  maxPrecedentDistance: 60,
};

export interface RtaProfile {
  skiType: SkiType;
  lengthCm: number;
  chosenLength?: number;
  chosenWaist?: number;
}

export interface RetrieveThenAdjustResult {
  recommendation: DesignRecommendation;
  ranked: RankedReference[];
  precedent: RankedReference | null;
  notes: string[];
}

function describePrecedent(rec: ReferenceDesign): string {
  return rec.serial !== null ? `serial ${rec.serial}` : `order ${rec.orderId}`;
}

function deferToDesigner(
  profile: RtaProfile,
  ranked: RankedReference[],
  reason: string,
  best: RankedReference | null,
): RetrieveThenAdjustResult {
  const unresolved = [reason];
  return {
    recommendation: {
      contractVersion: '1.0',
      payloadRef: `profile:${profile.skiType}:${profile.lengthCm}`,
      skiType: {
        value: profile.skiType,
        source: 'generated',
        explanation: 'From profile skiType; dims deferred to designer.',
      },
      length: {
        value: profile.lengthCm,
        source: 'generated',
        explanation: 'From profile length; dims deferred to designer.',
      },
      noseWidth: { value: 0, source: 'generated', explanation: 'Deferred to designer (no acceptable precedent).' },
      tailWidth: { value: 0, source: 'generated', explanation: 'Deferred to designer (no acceptable precedent).' },
      waistWidthRecommendation: { value: 0, source: 'generated', explanation: 'Deferred to designer (no acceptable precedent).' },
      flexPattern: { value: 'Balanced', source: 'generated', explanation: 'Deferred to designer (no acceptable precedent).' },
      construction: {
        value: {},
        source: 'generated',
        explanation: 'Deferred to designer (no acceptable precedent).',
      },
      validity: { geometric: 'warning', production: 'unchecked', notes: ['deferred: no acceptable precedent'] },
      fallbackUsed: 'defer-to-designer',
      unresolved,
    },
    ranked,
    precedent: best,
    notes: unresolved,
  };
}

export function retrieveThenAdjust(input: { profile: RtaProfile; lib: ReferenceLibrary; rules?: AdjustmentRules }): RetrieveThenAdjustResult {
  const rules = input.rules ?? DEFAULT_ADJUSTMENT_RULES;
  const ranked = rankByDistance({ skiType: input.profile.skiType, lengthCm: input.profile.lengthCm }, input.lib);

  if (ranked.length === 0) {
    return deferToDesigner(input.profile, ranked, 'library is empty - no precedent exists', null);
  }

  const top = ranked[0];
  if (top.distance.total > rules.maxPrecedentDistance) {
    return deferToDesigner(
      input.profile,
      ranked,
      `no precedent within threshold (best ${top.distance.total.toFixed(1)} > ${rules.maxPrecedentDistance})`,
      top,
    );
  }

  const rec = top.record;
  const pRef = { serial: rec.serial ?? undefined, orderFormId: rec.orderFormId };

  let lengthCm = rec.lengthCm;
  let lengthSource: RecommendationField<number>['source'] = 'precedent';
  let lengthExplanation = `Copied from precedent ${describePrecedent(rec)}.`;
  const unresolved: string[] = [];

  if (input.profile.chosenLength !== undefined) {
    const delta = Math.abs(input.profile.chosenLength - rec.lengthCm);
    if (delta <= rules.maxDeltaLengthCm) {
      lengthCm = input.profile.chosenLength;
      lengthSource = 'generated';
      lengthExplanation = `Bounded adjustment from precedent ${describePrecedent(rec)}: ${rec.lengthCm} -> ${lengthCm} cm (within ${rules.maxDeltaLengthCm} cm rule v${ADJUSTMENT_RULES_VERSION}).`;
    } else {
      unresolved.push(
        `chosen length ${input.profile.chosenLength} cm exceeds the ${rules.maxDeltaLengthCm} cm bound from precedent (${rec.lengthCm} cm) - kept precedent value`,
      );
    }
  }

  let waistMm = rec.waistWidthRecommendationMm;
  let waistSource: RecommendationField<number>['source'] = 'precedent';
  let waistExplanation = rec.waistWidthRecommendationMm === null
    ? `Precedent ${describePrecedent(rec)} had no waistRec.`
    : `Copied from precedent ${describePrecedent(rec)}.`;

  if (input.profile.chosenWaist !== undefined) {
    if (waistMm === null) {
      waistMm = input.profile.chosenWaist;
      waistSource = 'generated';
      waistExplanation = `Precedent had no waistRec - customer-stated waist ${input.profile.chosenWaist} mm used (provenance: generated, not precedent).`;
    } else if (Math.abs(input.profile.chosenWaist - waistMm) <= rules.maxDeltaWaistMm) {
      waistMm = input.profile.chosenWaist;
      waistSource = 'generated';
      waistExplanation = `Bounded adjustment from precedent: ${rec.waistWidthRecommendationMm} -> ${waistMm} mm (within ${rules.maxDeltaWaistMm} mm rule v${ADJUSTMENT_RULES_VERSION}).`;
    } else {
      unresolved.push(
        `chosen waist ${input.profile.chosenWaist} mm exceeds the ${rules.maxDeltaWaistMm} mm bound from precedent (${waistMm} mm) - kept precedent value`,
      );
    }
  }

  const recommendation: DesignRecommendation = {
    contractVersion: '1.0',
    payloadRef: `profile:${input.profile.skiType}:${input.profile.lengthCm}`,
    skiType: {
      value: rec.skiType as SkiType,
      source: 'precedent',
      precedentRef: pRef,
      explanation: `Type copied from precedent ${describePrecedent(rec)}.`,
    },
    length: { value: lengthCm, source: lengthSource, precedentRef: lengthSource === 'precedent' ? pRef : undefined, explanation: lengthExplanation },
    noseWidth: { value: rec.noseWidthMm, source: 'precedent', precedentRef: pRef, explanation: `Copied from precedent ${describePrecedent(rec)}.` },
    tailWidth: { value: rec.tailWidthMm, source: 'precedent', precedentRef: pRef, explanation: `Copied from precedent ${describePrecedent(rec)}.` },
    waistWidthRecommendation: { value: waistMm ?? 0, source: waistSource, precedentRef: waistSource === 'precedent' ? pRef : undefined, explanation: waistExplanation },
    flexPattern: {
      value: rec.flexPattern ?? 'Balanced',
      source: rec.flexPattern ? 'precedent' : 'generated',
      explanation: rec.flexPattern
        ? `Copied from precedent ${describePrecedent(rec)}.`
        : 'Precedent lacked flexPattern - default Balanced (provenance: generated).',
    },
    construction: {
      value: {
        coreMaterialId: rec.coreMaterialId == null ? undefined : Number(rec.coreMaterialId),
        overallStiffnessId: rec.stiffnessId == null ? undefined : Number(rec.stiffnessId),
        noseBlockId: rec.noseBlockId == null ? undefined : Number(rec.noseBlockId),
        tailBlockId: rec.tailBlockId == null ? undefined : Number(rec.tailBlockId),
        structuralLayerId: undefined,
      },
      source: 'precedent',
      explanation: 'Construction refs copied from the precedent record (structuralLayerId unavailable in corpus - unresolved).',
    },
    validity: { geometric: 'warning', production: 'unchecked', notes: ['validity computed in P1 D6/D7, not by D3'] },
    fallbackUsed: 'none',
    unresolved,
  };

  return { recommendation, ranked, precedent: top, notes: unresolved };
}
